import { useEffect, type RefObject } from 'react';
import { useShellLayout } from './useShellLayout';

function isScrollable(element: HTMLElement) {
  const style = window.getComputedStyle(element);
  const overflowY = style.overflowY;
  const canScroll = overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';
  return canScroll && element.scrollHeight - element.clientHeight > 48;
}

function findScrollContainer(node: HTMLElement | null): HTMLElement | null {
  let current = node?.parentElement ?? null;
  while (current) {
    if (isScrollable(current)) {
      return current;
    }
    current = current.parentElement;
  }

  const scrollingElement = document.scrollingElement as HTMLElement | null;
  if (scrollingElement && scrollingElement.scrollHeight - scrollingElement.clientHeight > 48) {
    return scrollingElement;
  }

  return null;
}

function easeOutCubic(value: number) {
  return 1 - Math.pow(1 - value, 3);
}

interface UseMobileAutoScrollHintOptions {
  targetRef: RefObject<HTMLElement | null>;
  sceneKey: string;
  enabled?: boolean;
  variant?: 'default' | 'bottom-up';
}

export function useMobileAutoScrollHint({ targetRef, sceneKey, enabled = true, variant = 'default' }: UseMobileAutoScrollHintOptions) {
  const { isMobileShell } = useShellLayout();

  useEffect(() => {
    if (!isMobileShell || !enabled || typeof window === 'undefined') {
      return undefined;
    }

    let frameId = 0;
    let retryTimer = 0;
    let canceled = false;

    const runAnimation = () => {
      if (canceled) {
        return;
      }

      const scrollContainer = findScrollContainer(targetRef.current);
      if (!scrollContainer) {
        retryTimer = window.setTimeout(runAnimation, 260);
        return;
      }

      const maxScroll = Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight);
      if (maxScroll < 64) {
        return;
      }

      if (variant === 'bottom-up') {
        scrollContainer.scrollTop = maxScroll;
      } else {
        scrollContainer.scrollTop = 0;
      }

      const target = Math.min(maxScroll, Math.max(110, Math.min(maxScroll * 0.24, 220)));
      const overshoot = Math.min(18, Math.max(8, target * 0.12));
      const totalDuration = 2500;
      const firstPhase = 0.88;
      const startTime = performance.now();

      const step = (now: number) => {
        if (canceled) {
          return;
        }

        const elapsed = now - startTime;
        const progress = Math.min(elapsed / totalDuration, 1);

        let nextScrollTop = target;
        if (progress < firstPhase) {
          const phaseProgress = easeOutCubic(progress / firstPhase);
          nextScrollTop = (target + overshoot) * phaseProgress;
        } else {
          const settleProgress = (progress - firstPhase) / (1 - firstPhase);
          const bounce = 1 - Math.pow(1 - settleProgress, 2);
          nextScrollTop = target + overshoot * (1 - bounce);
        }

        if (variant === 'bottom-up') {
          scrollContainer.scrollTop = maxScroll - nextScrollTop;
        } else {
          scrollContainer.scrollTop = nextScrollTop;
        }

        if (progress < 1) {
          frameId = window.requestAnimationFrame(step);
        }
      };

      frameId = window.requestAnimationFrame(step);
    };

    retryTimer = window.setTimeout(runAnimation, 180);

    return () => {
      canceled = true;
      window.clearTimeout(retryTimer);
      window.cancelAnimationFrame(frameId);
    };
  }, [enabled, isMobileShell, sceneKey, targetRef]);
}
